import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  getStatusLabel,
  type EvaluationStatus
} from '../../utils/statusLabels';
import api from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';

interface Subordinate {
  employee_id: string;
  employee_name: string;
  job_grade: string;
  job_title: string;
  department_name: string;
  department_type: string;
  evaluation_status: string;
  evaluation_id: string;
  period_id: string;
  /** 上長評価無効ルート（部門長が上長役を兼務）か */
  manager_eval_disabled: boolean;
  already_evaluated: boolean;
}

const DeptHeadEvaluationListPage: React.FC = () => {
  const navigate = useNavigate();
  const { periodId } = useParams<{ periodId: string }>();

  const [subordinates, setSubordinates] = useState<Subordinate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSubordinates();
  }, [periodId]);

  const fetchSubordinates = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get(
        `/dept-head-evaluation/subordinates/${periodId}`
      );

      if (response.data.success) {
        setSubordinates(response.data.data);
      }
    } catch (err) {
      console.error('部門長評価対象者取得エラー:', err);
      setError(extractErrorMessage(err, '評価対象者の取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  };


  const getDepartmentTypeLabel = (type: string) => {
    const typeMap: { [key: string]: string } = {
      tech: '技術',
      sales: '営業',
      admin: '管理',
    };
    return typeMap[type] || type;
  };

  const handleEvaluate = (subordinate: Subordinate) => {
    // 正しい順序: employeeId → periodId (バックエンドAPIに合わせる)
    navigate(`/dept-head-evaluation/${subordinate.employee_id}/${subordinate.period_id}`);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>読み込み中...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            部門長評価
          </Typography>
          <Typography variant="body2" color="text.secondary">
            評価対象者を選択し評価を入力してください
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {subordinates.length === 0 ? (
          <Alert severity="info">
            現在、評価対象者はいません
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>社員名</TableCell>
                  <TableCell>部門</TableCell>
                  <TableCell>部門種別</TableCell>
                  <TableCell>職級</TableCell>
                  <TableCell>役職</TableCell>
                  <TableCell>ステータス</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subordinates.map((subordinate) => {
                  const statusInfo = getStatusLabel(
                    subordinate.evaluation_status as EvaluationStatus,
                    undefined,
                    { managerEvalDisabled: subordinate.manager_eval_disabled }
                  );

                  return (
                    <TableRow key={subordinate.employee_id} hover>
                      <TableCell>{subordinate.employee_name}</TableCell>
                      <TableCell>{subordinate.department_name}</TableCell>
                      <TableCell>
                        <Chip
                          label={getDepartmentTypeLabel(subordinate.department_type)}
                          size="small"
                          color={
                            subordinate.department_type === 'tech'
                              ? 'primary'
                              : subordinate.department_type === 'sales'
                              ? 'secondary'
                              : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>{subordinate.job_grade}</TableCell>
                      <TableCell>{subordinate.job_title}</TableCell>
                      <TableCell>
                        <Chip
                          label={statusInfo.label}
                          color={statusInfo.chipColor}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          color={subordinate.already_evaluated ? "success" : "primary"}
                          size="small"
                          onClick={() => handleEvaluate(subordinate)}
                          disabled={
                            !subordinate.evaluation_status ||
                            subordinate.evaluation_status === 'root_defined' ||
                            subordinate.evaluation_status === 'goal_setting'
                          }
                        >
                          {subordinate.already_evaluated
                            ? '評価確認 (提出済)'
                            : subordinate.evaluation_status === 'dept_head_evaluated' ||
                              subordinate.evaluation_status === 'finalized'
                            ? '評価確認'
                            : '評価入力'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-start' }}>
          <Button variant="outlined" onClick={() => navigate('/dashboard')}>
            ダッシュボードに戻る
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default DeptHeadEvaluationListPage;
